import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAllUsers, getAllRoles, createUser, updateUser, deleteUser, GetAllDeleteUsers, restoreUser } from '../service/user';
import { CreateUserFormData, EditUserFormData, Role } from '../types/user';
import { User } from '../types';
import { message } from 'antd';
import { tokenManager } from '../api/tokenManager';

export const useUsers = () => {
  return useQuery<User[]>({
    queryKey: ['users'],
    queryFn: getAllUsers,
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserFormData) => createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] }); // Refresh user list after creation
      message.success('User created successfully!');
    },
    onError: () => {
      message.error('User not created!');
    },
  });
};

export const useEditUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EditUserFormData }) => updateUser(data, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success('User edited successfully!');
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success('User deleted successfully!');
    },
  });
};

export const useRoles = () => {
  const isAuthenticated = !!tokenManager.get('accessToken');

  return useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: getAllRoles,
    enabled: isAuthenticated, // Only run when authenticated
  });
};

export const useDeletedUsers = () => {
  return useQuery<User[]>({
    queryKey: ['deleted-users'],
    queryFn: GetAllDeleteUsers,
  });
}

export const useRestoreUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: restoreUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deleted-users'] });
    },
  });
};
